import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Trash2, Save, MoveUp, MoveDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function FormBuilder() {
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      placeholder: '',
      validation: ''
    };
    setFields([...fields, newField]);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index, updates) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    setFields(updated);
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (fields.length === 0) {
      toast.error('Add at least one field');
      return;
    }

    setSaving(true);
    try {
      await api.post('/api/forms/templates', {
        name: templateName,
        description: templateDescription,
        template_type: 'custom',
        fields_schema: fields
      });

      toast.success('Template created successfully!');
      navigate('/forms');
    } catch (error) {
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/forms')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Forms
        </Button>

        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
          Form Builder
        </h1>
        <p className="text-muted-foreground mt-1">Create custom form templates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Template Settings */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Template Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template_name">Template Name *</Label>
                <Input
                  id="template_name"
                  placeholder="e.g., Monthly Expense Report"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="template_desc">Description</Label>
                <Textarea
                  id="template_desc"
                  placeholder="Describe what this form is for..."
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-3">Fields: {fields.length}</p>
                <Button onClick={addField} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Field Builder */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Form Fields</CardTitle>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No fields yet</p>
                  <p className="text-sm mt-1">Click "Add Field" to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <Card key={field.id} className="bg-muted/50">
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline">Field {index + 1}</Badge>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => moveField(index, 'up')}
                                disabled={index === 0}
                              >
                                <MoveUp className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => moveField(index, 'down')}
                                disabled={index === fields.length - 1}
                              >
                                <MoveDown className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeField(index)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Field Label</Label>
                              <Input
                                placeholder="e.g., Full Name"
                                value={field.label}
                                onChange={(e) => updateField(index, { label: e.target.value })}
                                size="sm"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Field Type</Label>
                              <Select
                                value={field.type}
                                onValueChange={(value) => updateField(index, { type: value })}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="number">Number</SelectItem>
                                  <SelectItem value="textarea">Textarea</SelectItem>
                                  <SelectItem value="checkbox">Checkbox</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                placeholder="Hint text..."
                                value={field.placeholder || ''}
                                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                size="sm"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Validation (regex)</Label>
                              <Input
                                placeholder="e.g., ^\\d{9}$"
                                value={field.validation || ''}
                                onChange={(e) => updateField(index, { validation: e.target.value })}
                                size="sm"
                                className="font-mono text-xs"
                              />
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`required_${field.id}`}
                              checked={field.required}
                              onCheckedChange={(checked) => updateField(index, { required: checked })}
                            />
                            <Label htmlFor={`required_${field.id}`} className="text-sm cursor-pointer">
                              Required field
                            </Label>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
